import type { ReactNode } from 'react';
import { useState } from 'react';
import {
  Box,
  Flex,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import FigmaListTreeIcon from 'components/Icons/FigmaListTreeIcon';
import FigmaSearchIcon from 'components/Icons/FigmaSearchIcon';
import { getBuilderDimensions, getSelectedDimension } from 'redux/BuilderModeSlice';

import { accent, neutral, primaryAction, radius, shadow, text as textToken } from './designTokens';

interface MoveItemsPopoverProperties {
  children: ReactNode;
  onSelectDimension: (dimension: { id: number; name: string }) => void;
}

/**
 * Target-dimension picker for the "Move" action in `SelectionActionBar`.
 * Lists every other dimension in this model; picking one currently just
 * reports the choice back up (see `onSelectDimension`) — there's no backend
 * endpoint yet to actually move items across dimensions, so wiring that up
 * is a follow-up once one exists.
 */
const MoveItemsPopover = ({ children, onSelectDimension }: MoveItemsPopoverProperties) => {
  const [searchValue, setSearchValue] = useState('');
  const builderDimensions = useSelector(getBuilderDimensions) as
    | { id: number; name: string }[]
    | undefined;
  const selectedDimension: any = useSelector(getSelectedDimension);

  const targetDimensions = (builderDimensions || [])
    .filter((dimension) => dimension.id !== selectedDimension?.id)
    .filter((dimension) => dimension.name?.toLowerCase().includes(searchValue.toLowerCase()));

  return (
    <Popover placement='bottom-start' onClose={() => setSearchValue('')}>
      <PopoverTrigger>
        <Box>{children}</Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          border={`1px solid ${neutral.border}`}
          borderRadius={radius.overlay}
          boxShadow={shadow.overlay}
          bg='white'
          zIndex='popover'
          w='240px'
          p={0}
          overflow='hidden'
          _focus={{ boxShadow: shadow.overlay }}
          _focusVisible={{ boxShadow: shadow.overlay }}
        >
          <PopoverBody p={0}>
            <Box px='12px' pt='12px' pb='8px'>
              <Text fontSize='13px' fontWeight='500' color={primaryAction.bg} mb='9px'>
                Move to dimension
              </Text>
              <Flex
                alignItems='center'
                gap='6px'
                bg={neutral.surfaceMuted}
                border={`1px solid ${neutral.border}`}
                borderRadius={radius.control}
                px='9px'
                py='6px'
              >
                <FigmaSearchIcon width={12} height={12} color={textToken.muted} />
                <Input
                  variant='unstyled'
                  placeholder='Search dimensions'
                  fontSize='12px'
                  color={primaryAction.bg}
                  value={searchValue}
                  _placeholder={{ color: textToken.muted }}
                  onChange={(event) => setSearchValue(event.target.value)}
                />
              </Flex>
            </Box>

            <Stack spacing='1px' maxH='220px' overflowY='auto' px='6px' pb='6px'>
              {targetDimensions.length === 0 ? (
                <Text fontSize='12px' color={textToken.muted} px='6px' py='10px' textAlign='center'>
                  No matching dimensions
                </Text>
              ) : (
                targetDimensions.map((dimension) => (
                  <Flex
                    key={dimension.id}
                    as='button'
                    type='button'
                    alignItems='center'
                    gap='8px'
                    textAlign='left'
                    px='8px'
                    py='8px'
                    borderRadius={radius.control}
                    cursor='pointer'
                    transition='background 0.1s ease'
                    _hover={{ bg: accent.subtle }}
                    onClick={() => onSelectDimension(dimension)}
                  >
                    <FigmaListTreeIcon width={12} height={12} color={textToken.secondary} />
                    <Text fontSize='13px' color={primaryAction.bg} isTruncated>
                      {dimension.name}
                    </Text>
                  </Flex>
                ))
              )}
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default MoveItemsPopover;
